import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { FileText, BarChart3, Download, Eye } from "lucide-react";
import { Pagination, paginate } from "@/components/Pagination";

export default function InvestorReportingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [investor, setInvestor] = useState(null);
  const [statementPeriods, setStatementPeriods] = useState([]);
  const [fundName, setFundName] = useState("");
  const [frequency, setFrequency] = useState("quarterly");
  const [rollOnData, setRollOnData] = useState({ rows: [] });
  const [downloading, setDownloading] = useState(null);
  const [stmtPage, setStmtPage] = useState(1);
  const [rollOnPage, setRollOnPage] = useState(1);
  const [stmtPageSize, setStmtPageSize] = useState(50);
  const [rollOnPageSize, setRollOnPageSize] = useState(50);

  const previewStatement = async (periodId) => {
    if (!investor) return;
    try {
      const response = await API.get(`/investors/${investor.id}/statement/${periodId}?preview=1`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Failed to preview statement"); }
  };

  const loadData = useCallback(async () => {
    try {
      // Get investor record
      const invRes = await API.get("/investors");
      const inv = invRes.data[0];
      if (!inv) { setLoading(false); return; }
      setInvestor(inv);

      // Load statement periods and roll-on report in parallel
      const [periodsRes, rollOnRes] = await Promise.all([
        API.get(`/investors/${inv.id}/statement-periods`),
        API.get(`/funds/${inv.fund_id}/roll-on-report`),
      ]);
      setStatementPeriods(periodsRes.data.periods || []);
      setFundName(periodsRes.data.fund_name || "");
      setFrequency(periodsRes.data.frequency || "quarterly");
      setRollOnData(rollOnRes.data);
    } catch { toast.error("Failed to load reporting data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const downloadStatement = async (periodId, periodLabel) => {
    if (!investor) return;
    setDownloading(periodId);
    try {
      const response = await API.get(`/investors/${investor.id}/statement/${periodId}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${investor.name?.replace(/ /g, "_")}_-_${periodLabel.replace(/ /g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded statement for ${periodLabel}`);
    } catch { toast.error("Failed to download statement"); }
    finally { setDownloading(null); }
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="investor-reporting-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Reporting</h1>
        {fundName && (
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-muted-foreground text-sm">{fundName}</span>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0">{frequency}</Badge>
          </div>
        )}
      </div>

      <Tabs defaultValue="statements" className="space-y-5">
        <TabsList className="bg-secondary/50 border border-border" data-testid="investor-report-tabs">
          <TabsTrigger value="statements" className="gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Investor Statements
          </TabsTrigger>
          <TabsTrigger value="rollon" className="gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Roll-on Report
          </TabsTrigger>
        </TabsList>

        {/* Investor Statement Tab */}
        <TabsContent value="statements">
          <Card data-testid="statements-card">
            <CardHeader className="p-5 pb-3">
              <CardTitle className="font-heading text-lg">Investor Statements</CardTitle>
              <p className="text-sm text-muted-foreground">Download your capital account statements for each reporting period.</p>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              {statementPeriods.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No statements available yet. Statements are generated when performance data is uploaded.
                </div>
              ) : (() => {
                const { paginatedItems: pagedStmts, totalPages, safePage } = paginate(statementPeriods, stmtPage, stmtPageSize);
                return (
                  <>
                    <div className="space-y-2">
                      {pagedStmts.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3.5 rounded-lg bg-secondary/30 border border-border/50 group hover:border-primary/30 transition-colors" data-testid={`statement-${p.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                              <FileText className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{p.label}</p>
                              <p className="text-xs text-muted-foreground">Capital Account Statement</p>
                            </div>
                            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${p.status === "released" ? "text-emerald-600 dark:text-emerald-400 border-emerald-400/30" : "text-amber-600 dark:text-amber-400 border-amber-400/30"}`}>
                              {p.status === "released" ? "Released" : "Submitted"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs opacity-60 group-hover:opacity-100 transition-opacity"
                              onClick={() => previewStatement(p.id)}
                              data-testid={`preview-stmt-${p.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1.5" />
                              Preview
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="h-8 text-xs border border-border opacity-60 group-hover:opacity-100 transition-opacity"
                              onClick={() => downloadStatement(p.id, p.label)}
                              disabled={downloading === p.id}
                              data-testid={`download-stmt-${p.id}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1.5" />
                              {downloading === p.id ? "Downloading..." : "Download PDF"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setStmtPage} pageSize={stmtPageSize} onPageSizeChange={(v) => { setStmtPageSize(v); setStmtPage(1); }} />
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roll-on Report Tab */}
        <TabsContent value="rollon">
          <Card className="overflow-hidden" data-testid="investor-rollon-table">
            <CardHeader className="p-5 pb-3">
              <CardTitle className="font-heading text-lg">Life-to-Date Roll-on Report</CardTitle>
              <p className="text-sm text-muted-foreground">Your complete capital account activity across all reporting periods.</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/20 hover:bg-secondary/20 border-b border-border/50">
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-4 min-w-[100px]">Period</TableHead>
                      {rollOnData.rows?.some(r => r.class_description) && (
                        <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[80px]">Offering</TableHead>
                      )}
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[110px]">Beginning Bal</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Additions</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Gross Income</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Mgmt Fee</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Incentive Fee</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Redemptions</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[110px]">Ending Bal</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[70px] pr-4">RoR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rollOnData.rows?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-16 text-muted-foreground text-sm">
                          <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          No report data available yet.
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                      const { paginatedItems: pagedRows, totalPages, safePage } = paginate(rollOnData.rows || [], rollOnPage, rollOnPageSize);
                      return pagedRows.map((r, idx) => (
                      <TableRow key={idx} data-testid={`inv-rollon-row-${idx}`}>
                        <TableCell className="pl-4 text-sm font-medium">{r.period_label}</TableCell>
                        {rollOnData.rows?.some(row => row.class_description) && (
                          <TableCell className="text-xs text-muted-foreground">{r.class_description || "-"}</TableCell>
                        )}
                        <TableCell className="text-right text-sm tabular-nums">{fmt(r.beginning_balance)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.additions > 0 ? <span className="text-emerald-400">{fmt(r.additions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.total_income > 0 ? <span className="text-emerald-400">{fmt(r.total_income)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.management_fee > 0 ? <span className="text-amber-400">{fmt(r.management_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.incentive_fee > 0 ? <span className="text-violet-400">{fmt(r.incentive_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.redemptions > 0 ? <span className="text-red-400">{fmt(r.redemptions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums font-semibold">{fmt(r.ending_balance)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums pr-4">{r.rate_of_return !== 0 ? <span className={r.rate_of_return >= 0 ? "text-emerald-400" : "text-red-400"}>{r.rate_of_return.toFixed(2)}%</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                      </TableRow>
                    ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            {(() => {
              const { totalPages, safePage } = paginate(rollOnData.rows || [], rollOnPage, rollOnPageSize);
              return <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setRollOnPage} pageSize={rollOnPageSize} onPageSizeChange={(v) => { setRollOnPageSize(v); setRollOnPage(1); }} />;
            })()}
          </Card>
          <p className="text-xs text-muted-foreground mt-2">{rollOnData.rows?.length || 0} period{rollOnData.rows?.length !== 1 ? "s" : ""}</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
